import React from "react"
import { graphql } from "gatsby"

import Layout from "../layouts/Layout"
import Spacer from "../components/Spacer"
import Seo from "../components/Seo"
import Hero from "../components/home/Hero"
import Cta from "../components/Cta"
import About from "../components/home/About"
import Benefits from "../components/home/Benefits"
import Newsletter from "../components/home/Newsletter"
import PartnerLogos from "../components/home/PartnerLogos"
import Partners from "../components/home/Partners"
import Services from "../components/home/Services"
import Tech from "../components/home/Tech"

import Snippet from "../components/Snippet"

export const pageMetadataQuery = graphql`
  fragment PageMetadata on ContentfulPageMetadata {
    description: metaDescription
    keywords: metaKeywords
    title: pageTitle
    pagePath
    noIndex
  }
`

export const imageQuery = graphql`
  fragment ContentfulImageData on ContentfulAsset {
    svg {
      content
    }
    file {
      contentType
      details {
        image {
          width
          height
        }
      }
      url
    }
    # publicUrl
  }
`

export const pageQuery = graphql`
  query IndexPageQuery($language: String, $originalPath: String) {
    contentfulPageMetadata(
      pagePath: { eq: $originalPath }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
    productCollections: allContentfulProductCollection(
      sort: { sortOrder: ASC }
      filter: { node_locale: { eq: $language }, isOld: { ne: true } }
    ) {
      nodes {
        id
        node_locale
        name
        description
        links: products {
          ... on ContentfulProduct {
            name
            slug
            icon: iconSmall {
              ...ContentfulImageData
            }
          }
          # ... on ContentfulExternalProduct {
          #   name
          #   url
          #   icon: iconSmall {
          #     ...ContentfulImageData
          #   }
          # }
        }
        image {
          ...ContentfulImageData
          gatsbyImageData(
            height: 970
            width: 690
            quality: 80
            layout: FULL_WIDTH
          )
        }
      }
    }
    logos: contentfulClientCollection(
      codeId: { eq: "companyCollectionHome" }
      node_locale: { eq: $language }
    ) {
      clients {
        id
        name
        url
        logo {
          ...ContentfulImageData
          gatsbyImageData(
            height: 65
            width: 144
            quality: 75
            layout: CONSTRAINED
          )
        }
        logoAligned {
          ...ContentfulImageData
          gatsbyImageData(
            height: 65
            width: 144
            quality: 75
            layout: CONSTRAINED
          )
        }
        logoInlined {
          ...ContentfulImageData
          gatsbyImageData(
            height: 65
            width: 144
            quality: 75
            layout: CONSTRAINED
          )
        }
      }
    }
    benefitsWhy: contentfulBenefitCollection(
      codeId: { eq: "benefitCollectionHomeWhy" }
      node_locale: { eq: $language }
    ) {
      benefits {
        title
        icon {
          ...ContentfulImageData
        }
        description {
          childMarkdownRemark {
            html
          }
        }
      }
      node_locale
    }
    benefitsTech: contentfulBenefitCollection(
      codeId: { eq: "benefitCollectionHomeTech" }
      node_locale: { eq: $language }
    ) {
      benefits {
        title
        icon {
          ...ContentfulImageData
        }
        description {
          childMarkdownRemark {
            html
          }
        }
      }
      node_locale
    }
    testimonialAmex: contentfulTestimonial(
      codeId: { eq: "homeTestimonialAmex" }
      node_locale: { eq: $language }
    ) {
      id
      thumbnail {
        ...ContentfulImageData
        # gatsbyImageData(width: 1154, height: 792, layout: CONSTRAINED)
        gatsbyImageData(width: 1152, height: 648, layout: CONSTRAINED)
      }
      youtubeURL
      authorName
      authorTitle
      authorPhoto {
        ...ContentfulImageData
        gatsbyImageData(
          breakpoints: [60, 120]
          cropFocus: FACE
          resizingBehavior: THUMB
          height: 240
          width: 240
          quality: 75
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
      text {
        text
      }
      company {
        name
        logo {
          ...ContentfulImageData
        }
        logoInlined {
          ...ContentfulImageData
        }
      }
    }
    testimonialTomorrow: contentfulTestimonial(
      codeId: { eq: "homeTestimonialTomorrow" }
      node_locale: { eq: $language }
    ) {
      id
      thumbnail {
        ...ContentfulImageData
        # gatsbyImageData(width: 1154, height: 792, layout: CONSTRAINED)
        gatsbyImageData(width: 1152, height: 648, layout: CONSTRAINED)
      }
      youtubeURL
      authorName
      authorTitle
      authorPhoto {
        ...ContentfulImageData
        gatsbyImageData(
          breakpoints: [60, 120]
          cropFocus: FACE
          resizingBehavior: THUMB
          height: 240
          width: 240
          quality: 75
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
      text {
        text
      }
      company {
        name
        logo {
          ...ContentfulImageData
        }
        logoInlined {
          ...ContentfulImageData
        }
      }
    }
  }
`

const IndexPage = (props) => {
  const pageMetadata = props.data.contentfulPageMetadata
  const productCollections = props.data.productCollections.nodes
  const benefitsWhy = props.data.benefitsWhy.benefits
  const benefitsTech = props.data.benefitsTech.benefits
  const logos = props.data.logos.clients
  const testimonials = [
    props.data.testimonialAmex,
    props.data.testimonialVivid,
    props.data.testimonialTomorrow,
  ]

  return (
    <Layout pageId="home">
      <Seo {...pageMetadata} />
      <Hero />
      <Spacer height={50} />
      <PartnerLogos logos={logos} />
      <Spacer height={50} lg={160} />
      <About />
      <Spacer height={180} />
      <Services productCollections={productCollections} />
      <Spacer height={50} lg={160} />
      <Partners testimonials={testimonials} />
      <Spacer height={150} />
      <Tech benefits={benefitsTech} />
      <Spacer height={180} />
      <Benefits benefits={benefitsWhy} />
      <Cta
        header={Snippet({ id: "homeCTA", field: "header" })}
        linkText={Snippet({ id: "homeCTA", field: "linkText" })}
        linkUrl={Snippet({ id: "homeCTA", field: "linkUrl" })}
        highlightButton
      />
      <Newsletter />
    </Layout>
  )
}

export default IndexPage
